<script setup lang="ts">
import { Pipette } from 'lucide-vue-next'
import { Sketch } from '@ckpack/vue-color'
import { handleInputValue } from '@/utils/colorClass'
import useColorPicker from './useColorPicker'

const props = defineProps({
  label: {
    type: String,
    default: null
  },
  tip: {
    type: String,
    default: null
  },
  id: {
    type: String,
    required: true
  },
  modelValue: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

// Bridge: modelValue is a hex string WITH '#' (e.g. "#ff0000").
// Sketch accepts a hex string and emits an object { hex, hex8, ... }.
const colorModel = computed({
  get: () => props.modelValue || '#000000',
  set: (val: any) => {
    const hex: string = typeof val === 'string' ? val : (val?.hex ?? val?.hex8 ?? '')
    emit('update:modelValue', hex.startsWith('#') ? hex : `#${hex}`)
  }
})

const { getColorIfHexa, isEyeDropperSupported, useEyeDropper } = useColorPicker()

const updateColorFromInput = (newColor: any) => {
  emit('update:modelValue', getColorIfHexa(props.modelValue, handleInputValue(newColor)))
}

const openEyeDropper = async () => {
  if (!isEyeDropperSupported.value) return

  const { sRGBHex } = await useEyeDropper()
  if (sRGBHex) {
    emit('update:modelValue', sRGBHex)
  }
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="label">
      <label :for="id" class="block text-sm font-medium text-foreground">{{ label }}</label>
      <div v-if="tip" class="mt-2 text-xs font-normal text-muted-foreground">{{ tip }}</div>
    </div>

    <div class="grid auto-cols-max items-center grid-flow-col gap-2">
      <Popover>
        <PopoverTrigger as-child>
          <button
            type="button"
            class="size-10 rounded-control border border-input shadow-xs focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            :style="{ background: modelValue }"
          />
        </PopoverTrigger>
        <PopoverContent class="p-0 border-0 shadow-none bg-transparent w-auto">
          <Sketch
            v-model="colorModel"
            :disable-alpha="true"
            :disable-fields="true"
            :preset-colors="[]"
            class="sketch-no-presets"
          />
        </PopoverContent>
      </Popover>
      <Input
        :model-value="modelValue"
        :disabled="disabled"
        type="text"
        maxlength="7"
        class="w-24 min-w-0"
        @input="updateColorFromInput"
      />
      <Button
        v-if="isEyeDropperSupported"
        variant="outline"
        size="icon"
        title="Pick color from screen"
        @click="openEyeDropper"
      >
        <Pipette />
      </Button>
    </div>
  </div>
</template>

<style scoped>
.sketch-no-presets :deep(.vc-sketch-presets) {
  display: none;
}
</style>
